'use client';

import { useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import useInterval from './useInterval';
import ExchangeHistoryClient from '../utils/exchangeHistoryClient';
import { singletonHook } from 'react-singleton-hook';
import useS3Params from './useS3Params';

const DEFAULT_STATUS = {
	driftStatus: {
		status: 'green',
		message: '',
	} as DriftStore['driftStatus']['mainStatus'],
};

/**
 * Get the drift status from aws s3 bucket. Should be in the form `{status} | {message}` where status can be green|amber|red|'' and message can be any string.
 *
 * e.g:
 * - 'green | Things are back to normal'
 * - 'amber | The solana network is experiencing congestion'
 * - 'false'|'none'|'' (no status to display)
 * @returns
 */
const getStatus = (params: { status: string; message: string; alert: any }) => {
	try {
		const driftStatus = {
			status: params?.status ?? '',
			message: params?.message ?? '',
		} as DriftStore['driftStatus']['mainStatus'];

		return { driftStatus };
	} catch (e) {
		return DEFAULT_STATUS;
	}
};

const useDriftStatus = () => {
	const pathname = usePathname();
	const pollingMultiplier = useDriftStore((s) => s.pollingMultiplier);
	const s3Params = useS3Params();

	const setState = useDriftStore((s) => s.set);

	// Only check drift status on trading and portfolio pages where status matters
	const enabled = pathname
		? needsDataRequirement(pathname, 'trading') ||
			needsDataRequirement(pathname, 'portfolio')
		: false;

	useEffect(() => {
		if (!s3Params || !enabled) return;

		// Check status from s3
		const { driftStatus } = getStatus(s3Params);

		if (driftStatus.status) {
			setState((s) => {
				s.driftStatus.mainStatus = {
					...s.driftStatus.mainStatus,
					...driftStatus,
				};
			});
			return;
		}

		setState((s) => {
			s.driftStatus.mainStatus = {
				...s.driftStatus.mainStatus,
				...DEFAULT_STATUS.driftStatus,
			};
		});
	}, [s3Params, getStatus]);

	const refreshHistoryServerStatus = useCallback(async () => {
		if (!enabled) return;

		const status = await ExchangeHistoryClient.getServerStatus();
		setState((s) => {
			s.driftStatus.historyServerStatus.maintenanceMode =
				status?.body?.data?.maintenanceMode ?? false;
			s.driftStatus.historyServerStatus.maintenanceModeEstimateTs =
				status?.body?.data?.maintenanceModeEstimateTs ?? null;
		});
	}, [enabled]);

	useInterval(
		refreshHistoryServerStatus,
		enabled ? 30 * 1000 * pollingMultiplier : Number.MAX_SAFE_INTEGER
	);
};

export default singletonHook(() => {}, useDriftStatus);
